import { ApplicationMessage } from "graphql/type.ts";
import { DateMessage } from "lib/types/talkroom";
import { dateFormat } from "utils/date.util.ts";

// startからendまでの数字を返すGenerator関数
// 使い方: [...range(2, 5)] -> [2, 3, 4, 5]
export function* range(start: number, end: number): Generator<number> {
  for (let i = start; i <= end; i++) {
    yield i;
  }
}

/**
 * メッセージを日付に基づいてグループ化します。
 *
 * @param messages - グループ化するメッセージの配列
 * @returns グループ化されたメッセージを含むDateMessageオブジェクトの配列
 */
export function groupMessagesByDate(messages: ApplicationMessage[]): DateMessage[] {
  const dateMessagesMap: { [date: string]: ApplicationMessage[] } = {};

  messages
    .sort((a, b) => {
      const dateA = new Date(a.sendAt).getTime();
      const dateB = new Date(b.sendAt).getTime();
      return dateA - dateB;
    })
    .forEach((message) => {
      const datetime = dateFormat(message.sendAt, "YYYY/MM/DD HH:mm");
      const [date, time] = datetime.split(" ");

      // 時刻だけを残る
      const messageWithTimeOnly: ApplicationMessage = {
        ...message,
        sendAt: time,
      };

      if (!dateMessagesMap[date]) {
        dateMessagesMap[date] = [];
      }

      dateMessagesMap[date].push(messageWithTimeOnly);
    });
  let counter = 0;
  return Object.keys(dateMessagesMap)
    .sort((a, b) => a.localeCompare(b))
    .map((date) => ({
      date,
      key: `${date}-${counter++}`, // 唯一キー
      messages: dateMessagesMap[date],
    }));
}

/**
 * 2つ文字列を比較する
 * @param str1 比較される文字列
 * @param str2 比較する文字列
 * @returns ["同じ部分", "違う部分"]
 */
export const compareString = (str1: string, str2: string): [string, string] => {
  const arr1 = str1.split(/\s+/);
  const arr2 = str2.split(/\s+/);

  const sameElements: string[] = [];
  const differentElements: string[] = [];

  const minLength = Math.min(arr1.length, arr2.length);

  for (let i = 0; i < minLength; i++) {
    if (arr1[i] === arr2[i]) {
      sameElements.push(arr1[i]);
    } else {
      differentElements.push(arr2[i]);
    }
  }

  if (arr2.length > minLength) {
    differentElements.push(...arr2.slice(minLength));
  }

  return [sameElements.join(" "), differentElements.join(" ")];
};

/**
 * 配列が空かどうかを判定
 * @example
 * isEmpty([]) // true
 * isEmpty(null) // true
 * isEmpty(undefined) // true
 * isEmpty([1]) // false
 */
export const isEmptyArray = <T>(array?: T[]): boolean => {
  return array == null || array.length === 0;
};
